import {
    computed,
    inject,
    provide,
    toRaw,
    toValue,
    type ComputedRef,
    type InjectionKey,
    type MaybeRefOrGetter,
} from 'vue';

import { joinSelector, type SelectorSegment } from '@/scripts/helpers/selector';

type SelectorScope = {
    path: string,
    owner: object | null,
};

type SelectorField = {
    name: string,
};

const selectorScopeKey: InjectionKey<ComputedRef<SelectorScope>> = Symbol('selectorScope');

const emptyScope: SelectorScope = { path: '', owner: null };

const fieldScope = (parent: SelectorScope | undefined, field: SelectorField): SelectorScope => {
    const owner = toRaw(field);

    if (parent && parent.owner === owner) {
        return parent;
    }

    return {
        path: joinSelector(parent?.path, field.name),
        owner,
    };
};

const withField = (
    scope: SelectorScope | undefined,
    field: SelectorField | null,
): SelectorScope => (field ? fieldScope(scope, field) : scope ?? emptyScope);

const segmentScope = (
    parent: SelectorScope | undefined,
    segment: SelectorSegment,
): SelectorScope => {
    const ownSegment = joinSelector(segment);

    if (ownSegment === '') {
        return parent ?? emptyScope;
    }

    return {
        path: joinSelector(parent?.path, ownSegment),
        owner: null,
    };
};

export const provideFieldSelectorScope = (
    field: MaybeRefOrGetter<SelectorField | null>,
    segment: MaybeRefOrGetter<SelectorSegment> = null,
): ComputedRef<string> => {
    const parent = inject(selectorScopeKey, null);

    const scope = computed(() => withField(
        segmentScope(parent?.value, toValue(segment)),
        toValue(field),
    ));

    provide(selectorScopeKey, scope);

    return computed(() => scope.value.path);
};

export const useFieldSelector = (field: MaybeRefOrGetter<SelectorField | null>) => {
    const parent = inject(selectorScopeKey, null);

    const selectorPath = computed(() => withField(parent?.value, toValue(field)).path);

    return {
        selectorPath,
    };
};
